import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { map } from 'rxjs';

import { RpcRoute } from '@core-layout/app/models/rpc-route';
import { RouteService } from '@core-layout/app/services/route.service';
import { FoldersStoreWriteService } from '@folders/store/services/folders-store-write.service';
import { SectionName } from '@landing/enums/section-name.enum';
import { SectionConfig } from '@landing/models/section-config';
import { NavigationItem } from '@listing-details-navigation/models/navigation-item';
import { InternalListingDetailsNavigationService } from '@listing-details-navigation/services/listing-details-navigation.service';
import { ActivityClickedEventParams, ActivitiesClickedEventParams } from '@listings/models/events/activity-clicked-event-params';
import { ListingDetailsNavigationParams } from '@listings/models/events/listing-details-navigation-params';
import { ListingsService } from '@listings/services/listings.service';
import { ListingsStoreService } from '@listings/store/services/listings-store.service';
import { MediaViewerInitialParams } from '@media/components/media-viewer/models/media-viewer-initial-params';
import { MediaViewerService } from '@media/components/media-viewer/services/media-viewer.service';
import { SettingsStoreService } from '@settings/store/services/settings-store.service';
import { HybridService } from 'app/modules/core-modules/hybrid/hybrid.service';
import { MatchMediaService } from '@media/services/match-media.service';
import { CompanyConfiguration } from '@app-config/models/company-configuration';
import { ListingSelectionService } from '@listings/services/listing-selection.service';
import { RpcButtonComponent } from '../../../core-modules/controls/components/rpc-button/rpc-button.component';
import { CarouselComponent } from '../../../view-modules/view-base/components/carousel/carousel.component';
import { GalleryListingCardComponent } from '../../../view-modules/view-base/components/gallery-listing-card/gallery-listing-card.component';
import { LandingAppointmentCardComponent } from '../landing-appointment-card/landing-appointment-card.component';
import { LandingMessageCardComponent } from '../landing-message-card/landing-message-card.component';
import { LandingOpenHouseCardComponent } from '../landing-open-house-card/landing-open-house-card.component';
import { LandingBannerComponent } from '../landing-banner/landing-banner.component';
import { CustomSpinnerComponent } from '../../../core-modules/layout/custom-spinner/custom-spinner.component';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'landing-section',
    templateUrl: './landing-section.component.html',
    styleUrls: ['./landing-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RpcButtonComponent, CarouselComponent, GalleryListingCardComponent, LandingAppointmentCardComponent, LandingMessageCardComponent, LandingOpenHouseCardComponent, LandingBannerComponent, CustomSpinnerComponent, AsyncPipe, TranslateModule]
})
export class LandingSectionComponent<Data> {
    @Input() public sectionData: Data[];
    @Input() public config: SectionConfig;
    @Input() public isLoading = false;
    @Input() companyConfiguration: CompanyConfiguration;

    @Output() public bannerButtonClicked = new EventEmitter<void>();
    @Output() public sectionButtonCliked = new EventEmitter<void>();

    public readonly sectionName = SectionName;
    public readonly shouldHideOpenRentals$ = this.settingsStoreService.shouldHideOpenRentals$;
    public readonly activitiesOrNewMatchesInLoading$ = this.listingsStoreService.activitiesOrNewMatchesInLoading$;
    public readonly isHybrid$ = this.hybridService.isHybrid$;
    public readonly isMobile$ = this.matchMediaService.onMediaChange.pipe(map(mediaQuery => mediaQuery === 'xs' || mediaQuery === 'sm' || mediaQuery === 'md'));

    constructor(
        private readonly listingsService: ListingsService,
        private readonly mediaViewerService: MediaViewerService,
        private readonly settingsStoreService: SettingsStoreService,
        private readonly foldersStoreWriteService: FoldersStoreWriteService,
        private readonly listingsStoreService: ListingsStoreService,
        private readonly internalListingDetailsNavigationService: InternalListingDetailsNavigationService<NavigationItem>,
        private readonly routeService: RouteService,
        private readonly hybridService: HybridService,
        private readonly matchMediaService: MatchMediaService,
        private readonly listingSelectionService: ListingSelectionService,
    ) { }

    public onSectionButtonCliked(): void {
        this.sectionButtonCliked.emit();
    }

    public onBannerButtonClicked(): void {
        this.bannerButtonClicked.emit();
    }

    public onActivityChanged(activityClickedEventParams: ActivityClickedEventParams): void {
        this.listingsService.updateListingActivity(activityClickedEventParams);
    }

    public onActivitiesChanged(activitiesClickedEventParams: ActivitiesClickedEventParams): void {
        this.listingsService.updateListingActivities(activitiesClickedEventParams);
    }

    public onMediaViewerClicked(mediaViewerClickedEventParams: MediaViewerInitialParams, isDragging: boolean): void {
        if (isDragging) {
            return;
        }

        this.mediaViewerService.show(mediaViewerClickedEventParams);
    }

    public onListingDetailsClicked(listingDetailsNavigateEventParams: ListingDetailsNavigationParams, isDragging: boolean): void {
        if (isDragging) {
            return;
        }

        this.internalListingDetailsNavigationService.setNavigation();

        this.routeService.navigate(
            RpcRoute.Listing,
            { state: { tab: listingDetailsNavigateEventParams.tabId, previousPage: RpcRoute.FindHome } },
            RpcRoute.FindHome, listingDetailsNavigateEventParams.listingId
        ).catch(() => { });
    }

    public onFolderClicked(shouldUnselectListings: boolean, listingHashCode: number): void {
        this.foldersStoreWriteService.openFolderManagementDialog(shouldUnselectListings, [listingHashCode]);
    }
}
